import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  ColumnEvent,
  ColumnMovedEvent,
  DefaultMenuItem,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetMainMenuItems,
  GetMainMenuItemsParams,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  IServerSideGetRowsRequest,
  MenuItemDef,
  ModelUpdatedEvent,
  NewColumnsLoadedEvent,
  RowClickedEvent,
  RowDataUpdatedEvent,
  RowSelectedEvent,
  SelectionChangedEvent,
  SortChangedEvent,
  StoreRefreshedEvent,
  ValueFormatterParams
} from 'ag-grid-enterprise';
import { difference, merge, mergeWith } from 'lodash';
import { DateTime } from 'luxon';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { AuthService } from 'src/app/services/auth.service';
import { GridService } from 'src/app/services/grid.service';
import { UtilityService } from 'src/app/services/utility.service';

import { GridBase } from '../../classes/base-classes/grid-base/grid-base';
import { ActionCellRendererComponent } from '../grid-components/cell-renderer-components/action-cell-renderer/action-cell-renderer.component';
import { FieldMappingCellRendererComponent } from '../grid-components/cell-renderer-components/field-mapping-cell-renderer/field-mapping-cell-renderer.component';
import { StarIconCellRendererComponent } from '../grid-components/cell-renderer-components/star-icon-cell-renderer/star-icon-cell-renderer.component';
import { ExportToolPanelComponent } from '../grid-components/tool-panel-components/export-tool-panel/export-tool-panel.component';
import { ViveRibbonIconCellRendererComponent } from '../grid-components/vive-ribbon-icon-cell-renderer/vive-ribbon-icon-cell-renderer.component';

const TEXT_COLUMN_FILTER_DEBOUNCE_MS = 500;
const SERVER_SIDE_LOAD_DEBOUNCE_MS = 500;

@Component({
  selector: 'app-base-grid',
  templateUrl: './base-grid.component.html',
  styleUrls: ['./base-grid.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BaseGridComponent extends GridBase implements OnInit, OnChanges {
  protected _cdr = inject(ChangeDetectorRef);

  constructor(
    protected GridService: GridService,
    protected UtilityService: UtilityService,
    protected AuthService: AuthService
  ) {
    super();

    this.getRowId = (params: GetRowIdParams) =>
      String(params.data[this.gridPrimaryKeyFieldName || 'id']);
  }

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  context = {
    componentParent: this
  };

  components = {
    ActionCellRenderer: ActionCellRendererComponent,
    FieldMappingCellRenderer: FieldMappingCellRendererComponent,
    StarIconCellRenderer: StarIconCellRendererComponent,
    ViveRibbonIconCellRenderer: ViveRibbonIconCellRendererComponent
  };

  readonly noRowsOverlayTemplate =
    '<div class="vive-grid-empty">' +
    '<i class="bi bi-inbox"></i>' +
    '<p class="vive-grid-empty__title">No rows match these filters</p>' +
    '<p class="vive-grid-empty__hint">Your search criteria are still applied above. Adjust them and try again.</p>' +
    '</div>';

  readonly serverSideLoadDebounceMillis = SERVER_SIDE_LOAD_DEBOUNCE_MS;

  private rowClassRulesApplied = false;

  refreshRowClassRules(api: GridApi): void {
    api.setGridOption('rowClassRules', this.rowClassRules ?? {});
  }

  // This is used to construct a query object from grid information to be passed
  // to a backend service.
  constructGridQuery(
    gridInformation: { request: IServerSideGetRowsRequest },
    optional: { isCountQuery: boolean } = { isCountQuery: false }
  ) {
    if (!this.GridApi) {
      throw new Error('Grid Api has not been registered');
    }

    if (!this.gridType) {
      throw new Error('Missing Grid Type parameter.');
    }

    if (!this.columnDefs) {
      throw new Error('Missing Column Definitions for grid.');
    }

    // If we're constructing a count query, don't request attributes/columns from query.
    // May change to request attributes/columns if needed.
    const { isCountQuery } = optional;

    const { request } = gridInformation;

    const additional: {
      loadAllData: boolean;
      loadAllRows: boolean;
      request: IServerSideGetRowsRequest;
      uniqueKey: string | number;
      queryData?: { [key: string]: any };
      externalFilters?: Map<string, any>;
      suppressFieldDotNotation?: boolean;
      UniversalSearch: { text: string };
      isCountQuery: boolean;
      preventDistinctCount: boolean;
      omitGroupByOnCount: boolean;
    } = {
      loadAllData: this.loadAllData,
      loadAllRows: this.loadAllRows,
      uniqueKey: this.gridPrimaryKeyFieldName || 'id',
      request,
      suppressFieldDotNotation: this.suppressFieldDotNotation,
      UniversalSearch: this.UniversalSearch,
      isCountQuery,
      preventDistinctCount: this.preventDistinctCount,
      omitGroupByOnCount: this.omitGroupByOnCount
    };

    // Apply external filters to the query. External filters are filters that happen outside of the grid.
    if (this.Filters.size > 0) {
      Object.assign(additional, { externalFilters: this.Filters });
    }

    if (this.mergedQueryData) {
      Object.assign(additional, { queryData: this.mergedQueryData });
    }

    const columns = this.GridApi?.getColumns();

    if (!columns) {
      throw new Error('There was an error retrieving column state.');
    }

    const GridQuery = this.GridService.convertGridDefinitionToQuery(
      this.gridType,
      columns,
      additional
    );

    if (isCountQuery) {
      this.CurrentGridStateCountQuery = GridQuery;
      this.countGridQueryChanged.emit({
        GridQuery: this.CurrentGridStateCountQuery,
        GridRequest: request
      });
    } else {
      this.CurrentGridStateDataQuery = GridQuery;
      this.dataGridQueryChanged.emit({
        GridQuery: this.CurrentGridStateDataQuery,
        GridRequest: request
      });
    }

    return this.UtilityService.stringifyObjectsInQueryObject(
      structuredClone(GridQuery)
    );
  }

  handleFilterModel() {
    if (this.FilterModel && this.GridApi && this.GridApi.getColumnDefs()) {
      this.GridApi.setColumnsVisible(Object.keys(this.FilterModel), true);
      this.GridApi.setFilterModel(this.FilterModel);
    }
  }

  handleSortState() {
    if (this.SortState && this.GridApi && this.GridApi.getColumnDefs()) {
      this.GridApi.applyColumnState(this.SortState);
    }
  }

  // Handles the header checkbox initial selection state for server side pagination,
  // if the header checkbox exists.
  handleSelectionState() {
    if (
      this.GridApi &&
      this.rowModelType === 'serverSide' &&
      this.rowSelectionType === 'multiple'
    ) {
      this.GridApi.setServerSideSelectionState({
        selectAll:
          this.totalRowCount > 0 &&
          this.selectedRowsPrimaryKeys.size > 0 &&
          this.selectedRowsPrimaryKeys.size === this.totalRowCount,
        toggledNodes: []
      });
    }
  }

  // This is called immediately after the Api has been initialized by ag grid.
  sortColumnsInToolPanel() {
    if (this.GridApi) {
      const cols = this.GridApi.getColumnDefs();
      if (cols) {
        cols.sort((a, b) => {
          const { colId: ColumnAColId, headerName: ColumnAHeader } =
            a as ColDef;
          const { colId: ColumnBColId, headerName: ColumnBHeader } =
            b as ColDef;
          if (
            ColumnAColId === '$actionColumn' ||
            (ColumnAColId === '$starIconColumn' &&
              ColumnBColId !== '$actionColumn') ||
            (ColumnBColId === '$starIconColumn' &&
              ColumnAColId === '$actionColumn') ||
            (ColumnAHeader || '').toLowerCase() >=
              (ColumnBHeader || '').toLowerCase()
          ) {
            return 1;
          }

          return -1;
        });
        const ColumnToolPanel = this.GridApi?.getToolPanelInstance('columns');
        ColumnToolPanel?.setColumnLayout(cols);
      }
    }
  }

  setApis({ GridApi }: { GridApi: GridApi<any> }) {
    Object.assign(this, { GridApi });
    this.sortColumnsInToolPanel();
    this.handleFilterModel();
    this.handleSortState();
  }

  updateRowSelections() {
    if (!this.GridApi) {
      throw new Error('Grid Api has not been registered.');
    }

    this.selectedRowsPrimaryKeys.forEach((rowPrimaryKey) => {
      if (
        typeof rowPrimaryKey === 'string' ||
        typeof rowPrimaryKey === 'number'
      ) {
        const rowNode = this.GridApi!.getRowNode(rowPrimaryKey.toString());
        if (rowNode) {
          rowNode.setSelected(true);
        }
      }
    });
  }

  storeRefreshed(event: StoreRefreshedEvent) {
    if (!this.GridApi) {
      throw new Error('Grid Api has not been registered.');
    }

    if (this.exportAfterDataReload === 'csv') {
      this.GridApi.exportDataAsCsv({
        allColumns: this.loadAllData,
        processCellCallback: ({ column, value }) => {
          if (column.getColDef().filter === 'agDateColumnFilter' && value) {
            return new Date(value).toLocaleDateString('en-US');
          }

          return value;
        }
      });
    }

    if (this.exportAfterDataReload === 'excel') {
      this.GridApi.exportDataAsExcel();
    }

    // When choosing to export all data, the grid loads everything. Clear the filters.
    // This may be changed, depending on if the team likes this design.
    if (
      ['csv', 'excel'].includes(this.exportAfterDataReload!) &&
      this.loadAllData
    ) {
      Object.assign(this.UniversalSearch, { text: '' });
      this.GridApi?.setFilterModel(null);
    }

    Object.assign(this, {
      loadAllData: false,
      exportAfterDataReload: null,
      loadAllRows: false
    });

    this.updateRowSelections();

    this.refreshRowClassRules(this.GridApi);

    if (this.reloadGridData) {
      this.reloadGridDataChange.emit(false);
    }

    // Refresh happened, decrement counter.
    if (this.refreshCounter) {
      this.refreshCounter -= 1;
    }

    // If we need to refresh again, go again.
    if (this.refreshCounter) {
      this.GridApi?.refreshServerSide();
    }

    this._cdr.markForCheck();
  }

  // Don't use this unless you have no other choice. Read the documentation.
  reloadGrid() {
    this.showGrid = false;
    this._cdr.markForCheck();
    return new Observable((subscriber) => {
      setTimeout(() => {
        this.showGrid = true;
        this._cdr.markForCheck();
        subscriber.next('complete');
      });
    });
  }

  applyGridState() {
    if (this.GridApi) {
      const SavedGridState = localStorage.getItem(this.gridName);
      if (SavedGridState) {
        this.initialState = JSON.parse(SavedGridState);
        this.reloadGrid().subscribe();
      }
    }
  }

  saveGridState() {
    if (this.GridApi) {
      localStorage.setItem(
        this.gridName,
        JSON.stringify(this.GridApi.getState())
      );
    }
  }

  restoreOriginalGridState() {
    this.initialState = undefined;
    return this.reloadGrid();
  }

  // Need to load data from the server.
  columnVisibilityEvent = (event: ColumnEvent) => {
    if (!this.GridApi) {
      throw new Error('Grid Api has not been registered.');
    }

    if (!this.refreshCounter) {
      this.GridApi.refreshServerSide();
    }

    this.refreshCounter += 1;
  };

  filterChanged(_event: FilterChangedEvent) {
    this.filterChangeEvent.emit(true);
    this.resetFilterModelChange.emit(false);
  }

  hasActiveSearchCriteria(): boolean {
    if (this.UniversalSearch.text?.trim()) {
      return true;
    }

    if (this.GridApi) {
      const filterModel = this.GridApi.getFilterModel();
      return Object.keys(filterModel || {}).length > 0;
    }

    return false;
  }

  columnMoved(event: ColumnMovedEvent) {
    // Unused
  }

  sortChanged(event: SortChangedEvent) {
    // Unused
  }

  // When the columns are initialized by the grid, set up visibility change listener.
  // When column visibility is changed, load the applicable data from the server.
  onNewColumnsLoaded(event: NewColumnsLoadedEvent) {
    const { api } = event;
    api.sizeColumnsToFit();
    this.setApis({ GridApi: api });

    if (!this.GridApi) {
      throw new Error('Grid Api has not been registered.');
    }

    const colDefs = this.GridApi.getColumnDefs();

    if (!colDefs) {
      throw new Error('Colum definitions have not been initialized.');
    }

    // Assign column visibility function to load data for column(s) from server
    colDefs.forEach((colDef: ColDef) => {
      const col = this.GridApi?.getColumn(colDef.colId!);
      if (!col) {
        throw new Error('There was an error retrieving column data.');
      }
      col.removeEventListener('visibleChanged', this.columnVisibilityEvent);
      col.addEventListener('visibleChanged', this.columnVisibilityEvent);
    });
  }

  mergeSuppliedAndAdditionalQueryData() {
    if (this.additionalQueryData) {
      this.mergedQueryData = mergeWith(
        structuredClone(this.additionalQueryData),
        structuredClone(this.suppliedQueryData),
        (sourceValue, targetValue) => {
          if (Array.isArray(sourceValue)) {
            const sourceValueSet = new Set(sourceValue);
            targetValue.forEach((v: any) => {
              sourceValueSet.add(v);
            });
            return Array.from(sourceValueSet);
          }
          return;
        }
      );
    } else {
      this.mergedQueryData = Object.assign(
        structuredClone(this.additionalQueryData) || {},
        structuredClone(this.suppliedQueryData) || {}
      );
    }
  }

  // Used to process grid columns after loading from backend or stored state.
  /**
   * Hook for a grid to adjust the column definitions it was served. No-op by
   * default; see the call site in `processGridColumns` for why it exists.
   */
  protected applyColumnOverrides(ColDefs: ColDef<any>[]): ColDef<any>[] {
    return ColDefs;
  }

  processGridColumns(ColDefs: ColDef<any>[], api: GridApi) {
    if (this.suppliedQueryData) {
      this.mergeSuppliedAndAdditionalQueryData();
    }

    // Defensive de-dup: if the backend grid config sends two columns with
    // the same `field`, AG Grid silently renames the second's colId to
    // `${field}_1`, which then renders as a duplicate header in the UI.
    // Drop dupes here so the misconfigured server-side definition doesn't
    // leak into the rendered grid. Logs the dropped column so the
    // underlying config can still be fixed at source.
    const seenFields = new Set<string>();
    for (let i = ColDefs.length - 1; i >= 0; i--) {
      const { field, colId } = ColDefs[i] as ColDef;
      const key = field || colId;
      if (!key || key === '$actionColumn') continue;
      if (seenFields.has(key)) {
        console.warn(
          `[base-grid] Dropping duplicate grid column field="${key}" ` +
            `on grid "${this.gridType}/${this.gridName}". ` +
            `Check the backend grid config — two columns share the same field.`
        );
        ColDefs.splice(i, 1);
      } else {
        seenFields.add(key);
      }
    }

    // Default-header guard: any column whose backend config omitted
    // `headerName` would render with a blank header and feel like a
    // ghost column. Derive a readable label from `field` so the user
    // always sees what the column represents. The action column is
    // intentionally header-less and is whitelisted.
    ColDefs.forEach((colDef) => {
      const { field, colId, headerName } = colDef as ColDef;
      if (headerName !== undefined && headerName !== '') return;
      const id = field || colId;
      if (!id || id === '$actionColumn') return;
      // Convert camelCase / dot.path → Title Case words.
      const label = id
        .split('.')
        .pop()!
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/\s+/g, ' ')
        .trim();
      colDef.headerName = label;
    });

    const removeActionColumn = () => {
      ColDefs.forEach((colDef, index) => {
        const { colId } = colDef as ColDef;
        if (colId === '$actionColumn') {
          ColDefs.splice(index, 1);
        }
      });
    };

    // If 1 (or less) action(s) exists on the action column, remove the column and default to row-clicking
    // for functionality.
    if (this.ColumnIdToColumnActions) {
      const { ['$actionColumn']: actions } = this.ColumnIdToColumnActions;
      if (
        actions.length === 0 ||
        (actions.length === 1 && actions[0] === 'view')
      ) {
        removeActionColumn();
      }
    } else {
      removeActionColumn();
    }

    let GridStateColumns: Set<string> | undefined = undefined;
    if (this.initialState) {
      const { columnOrder } = this.initialState;

      if (columnOrder) {
        const { orderedColIds } = columnOrder;
        GridStateColumns = new Set(orderedColIds);
      }
    }

    // Per-grid presentation tweaks, applied BEFORE the shared processing below
    // so an adjusted column still receives the valueGetter, filter wiring and
    // grid-state registration every other column gets.
    //
    // This exists because several grids take their column config from the
    // backend, which cannot name a cellRenderer: the API deploys before this
    // app, so a renderer named server-side would be missing here until the
    // frontend catches up. Naming it on this side keeps the two independent.
    this.applyColumnOverrides(ColDefs);

    const GridColumnsToAddToGridState: ColDef[] = [];
    const GridColumnIds: string[] = [];
    ColDefs.forEach((colDef) => {
      const { field, allowedAggFuncs, type, cellDataType } = colDef;

      if (cellDataType === 'date') {
        colDef.valueFormatter = (params) => {
          const { value } = params;

          if (value && value instanceof Date) {
            const LuxonDate = DateTime.fromJSDate(value);
            if (type === 'datetime') {
              return LuxonDate.toFormat('MM/dd/yyyy HH:mm:ss').toString();
            }

            return LuxonDate.toFormat('MM/dd/yyyy').toString();
          } else {
            return '';
          }
        };
      }

      // The action column that VIVE defines in various grids won't be configured with a 'field' property.
      if (field) {
        if (allowedAggFuncs) {
          Object.assign(colDef, { enableValue: true });
        }
        Object.assign(colDef, { colId: field });

        if (typeof type === 'string' && type === 'boolean') {
          colDef.valueFormatter = (params) => {
            return params.value ? 'Yes' : 'No';
          };

          const { filter } = colDef;

          if (filter === 'agSetColumnFilter') {
            merge(colDef, {
              filterParams: {
                values: [1, 0],
                valueFormatter: (params: ValueFormatterParams) =>
                  params.value === 1 ? 'Yes' : 'No'
              }
            });
          }
        }

        if (colDef.filter === 'agTextColumnFilter') {
          merge(colDef, {
            filterParams: {
              ...((colDef.filterParams as Record<string, unknown>) || {}),
              debounceMs: TEXT_COLUMN_FILTER_DEBOUNCE_MS
            }
          });
        }

        colDef.valueGetter = (params) => {
          if (!this.GridApi) {
            throw new Error('Grid Api has not been registered.');
          }
          const col = this.GridApi?.getColumn(colDef.colId!);
          if (!col) {
            throw new Error('There was an error retrieving column data.');
          }
          const aggFunc = col.getAggFunc();
          const isAggregationActive = col.isValueActive();

          if (aggFunc && isAggregationActive) {
            return params.data[`${aggFunc}(${field})`];
          } else {
            if (this.suppressFieldDotNotation) {
              return params.data[field];
            } else {
              return field.split('.').reduce((accum, field) => {
                if (accum) {
                  return accum[field];
                }
                return null;
              }, params.data);
            }
          }
        };
      }

      const { colId } = colDef;

      if (colId) {
        GridColumnIds.push(colId);
        if (GridStateColumns && !GridStateColumns.has(colId)) {
          GridColumnsToAddToGridState.push(colDef);
        }
      }

      // Don't need filtering on the action column.
      if (colId && colId === '$actionColumn') {
        Object.assign(colDef, {
          filter: false,
          sortable: false,
          suppressHeaderMenuButton: true
        });
      }
    });

    // Remove suppressed columns entirely so they cannot be re-added via the
    // column tool panel (unlike defaultHiddenColumns, which only sets hide).
    if (this.suppressedColumns && this.suppressedColumns.length > 0) {
      const suppressedSet = new Set(this.suppressedColumns);
      for (let i = ColDefs.length - 1; i >= 0; i--) {
        const field = ColDefs[i].field;
        if (field && suppressedSet.has(field)) {
          ColDefs.splice(i, 1);
        }
      }
    }

    // Apply default hidden columns if specified by the parent component.
    if (this.defaultHiddenColumns && this.defaultHiddenColumns.length > 0) {
      const hiddenSet = new Set(this.defaultHiddenColumns);
      ColDefs.forEach((colDef) => {
        if (colDef.field && hiddenSet.has(colDef.field)) {
          colDef.hide = true;
        }
      });
    }

    this.setColumnDefs(ColDefs);

    this.refreshRowClassRules(api);

    if (this.ServerSideDataSource && this.rowModelType === 'serverSide') {
      api.setGridOption('serverSideDatasource', this.ServerSideDataSource);
      this._cdr.markForCheck();
    }

    const GridColumnsToRemoveFromGridState = difference(
      Array.from(GridStateColumns || []),
      GridColumnIds
    ).map((colId) => ({ colId }));
    if (
      this.initialState &&
      (GridColumnsToAddToGridState.length > 0 ||
        GridColumnsToRemoveFromGridState.length > 0)
    ) {
      const { columnOrder, columnSizing, columnVisibility } = this.initialState;

      const InitialStateUpdates: {
        orderedColIds?: Set<string>;
        columnSizingModel?: Map<
          string,
          { colId: string; flex?: number; width?: number }
        >;
        hiddenColIds?: Set<string>;
      } = {};

      if (columnOrder) {
        const { orderedColIds } = columnOrder;
        Object.assign(InitialStateUpdates, {
          orderedColIds: new Set(orderedColIds)
        });
      }

      if (columnSizing) {
        const { columnSizingModel } = columnSizing;
        const ColumnSizingModelMap = new Map<
          string,
          { colId: string; flex?: number; width?: number }
        >([]);
        columnSizingModel.forEach((ColumnSizeState) => {
          const { colId } = ColumnSizeState;
          ColumnSizingModelMap.set(colId, ColumnSizeState);
        });
        Object.assign(InitialStateUpdates, {
          columnSizingModel: ColumnSizingModelMap
        });
      }

      if (columnVisibility) {
        const { hiddenColIds } = columnVisibility;
        Object.assign(InitialStateUpdates, {
          hiddenColIds: new Set(hiddenColIds)
        });
      }

      const { orderedColIds, columnSizingModel, hiddenColIds } =
        InitialStateUpdates;
      GridColumnsToRemoveFromGridState.forEach((ColDef) => {
        const { colId } = ColDef;
        if (colId) {
          if (orderedColIds && orderedColIds.has(colId)) {
            orderedColIds.delete(colId);
          }

          if (columnSizingModel && columnSizingModel.has(colId)) {
            columnSizingModel.delete(colId);
          }

          if (hiddenColIds && hiddenColIds.has(colId)) {
            hiddenColIds.delete(colId);
          }
        }
      });

      GridColumnsToAddToGridState.forEach((ColDef) => {
        const { colId, flex, width } = ColDef;
        if (colId) {
          if (orderedColIds && !orderedColIds.has(colId)) {
            orderedColIds.add(colId);

            if (orderedColIds.has('$actionColumn')) {
              orderedColIds.delete('$actionColumn');
              orderedColIds.add('$actionColumn');
            }
          }

          if (columnSizingModel && !columnSizingModel.has(colId)) {
            columnSizingModel.set(colId, { colId, flex, width });
          }

          if (hiddenColIds && !hiddenColIds.has(colId)) {
            hiddenColIds.add(colId);
          }
        }
      });

      if (columnOrder && orderedColIds) {
        Object.assign(columnOrder, {
          orderedColIds: Array.from(orderedColIds)
        });
      }

      if (columnSizing && columnSizingModel) {
        Object.assign(columnSizing, {
          columnSizingModel: Array.from(columnSizingModel.values())
        });
      }

      if (columnVisibility && hiddenColIds) {
        Object.assign(columnVisibility, {
          hiddenColIds: Array.from(hiddenColIds)
        });
      }

      localStorage.setItem(this.gridName, JSON.stringify(this.initialState));
    }
  }

  onGridReady(params: GridReadyEvent) {
    const { api } = params;
    this.setApis({ GridApi: api });

    this.updateRowSelections();

    const SavedGridState = localStorage.getItem(this.gridName);
    if (SavedGridState) {
      this.initialState = JSON.parse(SavedGridState);
    }

    // If there is no saved grid state, no column definitions, or a filter model, use the default
    // state of the grid.
    if (
      !this.initialState ||
      !this.GridApi?.getColumnDefs() ||
      (this.FilterModel && Object.keys(this.FilterModel).length > 0)
    ) {
      this.GridService.getGridColumns(this.gridType, this.gridName).subscribe({
        next: (data: ColDef<any>[]) => {
          this.processGridColumns(data, api);
          this._cdr.markForCheck();
        }
      });
    } else {
      // Process columns for initial grid state.
      this.processGridColumns(
        this.GridApi?.getColumnDefs() as ColDef<any>[],
        api
      );
      this._cdr.markForCheck();
    }
  }

  // THIS ONLY WORKS FOR CLIENT-SIDED GRIDS (Should not work with server-sided pagination)
  onRowDataUpdated(event: RowDataUpdatedEvent) {
    this.refreshRowClassRules(event.api);

    // MOVE THIS TO WHEN DATA IS LOADED. NEED TO CREATE HOOK.
    if (this.rowModelType === 'clientSide') {
      event.api.forEachNode((node: IRowNode<any>, index: number) => {
        if (
          this.selectedRowsPrimaryKeys.has(
            node.data[this.gridPrimaryKeyFieldName || 'id']
          )
        ) {
          node.setSelected(true);
        }
      });
    }
  }

  // Used in server-side pagination to display row data updates.
  onModelUpdated(event: ModelUpdatedEvent) {
    this.updateRowSelections();

    if (this.reloadGridData) {
      this.reloadGridDataChange.emit(false);
    }

    if (this.GridApi) {
      this.totalRowCount = this.GridApi.getDisplayedRowCount();
      // VPDW-3111: AG Grid only auto-toggles the no-rows overlay for
      // the client-side row model. For server-side we trigger it here
      // so the branded empty state actually renders.
      if (this.totalRowCount === 0) {
        if (this.hasActiveSearchCriteria()) {
          this.showFilteredEmptyGrid = true;
          this.GridApi.hideOverlay();
        } else {
          this.showFilteredEmptyGrid = false;
          this.GridApi.showNoRowsOverlay();
        }
      } else {
        this.showFilteredEmptyGrid = false;
        this.GridApi.hideOverlay();
      }
    }

    this._cdr.markForCheck();
  }

  // Used when the grid first loads data.
  onFirstDataRendered(event: FirstDataRenderedEvent) {
    this.updateRowSelections();
    event.api.sizeColumnsToFit();
    this._cdr.markForCheck();
  }

  setColumnDefs(columnDefs: ColDef[]) {
    if (this.useCheckboxColumn) {
      this.columnDefs = [
        { checkboxSelection: true, headerCheckboxSelection: true },
        ...columnDefs
      ];
    } else {
      this.columnDefs = columnDefs;
    }
    this._cdr.markForCheck();
  }

  isRowSelectable = (node: IRowNode) => {
    if (!this.selectionEnabled) {
      const {
        data: { id }
      } = node;
      return this.selectedRowsPrimaryKeys.has(id);
    } else {
      if (node.isSelected()) {
        return !this.suppressRowClickSelection;
      }

      return true;
    }
  };

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectionChanged.emit(event);
  }

  onRowSelected(event: RowSelectedEvent) {
    this.rowSelectionChanged.emit(event);
    const { source, node } = event;
    const { data } = node;
    const rowIdentifier = data[this.gridPrimaryKeyFieldName || 'id'];

    // We want to process this when a user selects a checkbox.
    if (source !== 'api') {
      if (this.rowSelectionType === 'single') {
        if (node.isSelected()) {
          this.selectedRowsPrimaryKeys.add(rowIdentifier);
        } else {
          this.selectedRowsPrimaryKeys.delete(rowIdentifier);
        }
      } else {
        if (node.isSelected()) {
          if (!this.selectedRowsPrimaryKeys.has(rowIdentifier)) {
            this.selectedRowsPrimaryKeys.add(rowIdentifier);
          } else {
            this.selectedRowsPrimaryKeys.delete(rowIdentifier);
          }
        } else {
          this.selectedRowsPrimaryKeys.delete(rowIdentifier);
        }
      }
    }

    if (this.selectedRowsPrimaryKeys.size === this.totalRowCount) {
      this.handleSelectionState();
    }
  }

  getMainMenuItems: GetMainMenuItems = (params: GetMainMenuItemsParams) => {
    const LoggedInUser = this.AuthService.getUser();

    const DefaultMenuItems: DefaultMenuItem[] = [
      'sortAscending',
      'sortDescending',
      'sortUnSort',
      'autoSizeThis',
      'autoSizeAll',
      'resetColumns'
    ];

    const MenuItems: (MenuItemDef | DefaultMenuItem)[] = [
      ...DefaultMenuItems,
      {
        name: 'Save Grid',
        action: (params) => {
          this.saveGridState();
        }
      },
      {
        name: 'Load Grid',
        action: (params) => {
          this.applyGridState();
        }
      },
      {
        name: 'Reset Grid',
        action: (params) => {
          this.restoreOriginalGridState().subscribe();
        }
      }
    ];

    if (LoggedInUser) {
      const { UserAccountRole, UserAccountType } = LoggedInUser;

      if (!UserAccountRole) {
        throw new Error('Missing User Permission.');
      }

      if (!UserAccountType) {
        throw new Error('Missing User Type.');
      }

      const { role } = UserAccountRole;

      if (!role) {
        throw new Error('Missing User permission role.');
      }

      const AdminAccountRoles: Set<UserAccountRoles> = new Set([
        'ADMIN_ADMIN',
        'ADMIN_PRIME_TEAM',
        'ADMIN_EMAILS_TEAM',
        'ADMIN_INSURANCE_TEAM',
        'ADMIN_FRAUD_TEAM',
        'ADMIN_CUSTOMER_SERVICE_TEAM',
        'ADMIN_PROJECT_MANAGEMENT_TEAM'
      ]);

      if (AdminAccountRoles.has(role)) {
        const AdminDefaultMenuItems: DefaultMenuItem[] = [
          'valueAggSubMenu',
          'rowGroup',
          'rowUnGroup'
        ];

        MenuItems.unshift(...AdminDefaultMenuItems);
      }
    }

    return MenuItems;
  };

  ngOnInit(): void {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(400))
      .subscribe(() => {
        this.GridApi?.refreshServerSide({ purge: true });
      });

    if (this.overrideAdditionalQueryData) {
      this.additionalQueryData = this.overrideAdditionalQueryData;
    }

    if (!this.selectionEnabled) {
      this.suppressRowClickSelection = true;
    }

    const LoggedInUser = this.AuthService.getUser();

    if (LoggedInUser) {
      const { UserAccountRole, UserAccountType } = LoggedInUser;

      if (!UserAccountRole) {
        throw new Error('Missing User Permission.');
      }

      if (!UserAccountType) {
        throw new Error('Missing User Type.');
      }

      const { name } = UserAccountType;

      if (
        (this.gridType !== 'Company' ||
          (this.gridType === 'Company' && name !== 'Vendor')) &&
        typeof this.sideBar === 'object'
      ) {
        this.sideBar.toolPanels?.push({
          id: 'export',
          labelDefault: 'Export',
          labelKey: 'export',
          iconKey: 'export',
          toolPanel: ExportToolPanelComponent
        });
      }
    }

    this.mergeSuppliedAndAdditionalQueryData();

    this.handleFilterModel();
    this.handleSortState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {
      suppliedQueryData,
      resetFilterModel,
      reloadGridData,
      sortState,
      FilterModel,
      rowClassRules
    } = changes;

    // The parent swapped in a different set of rules — push them to the grid.
    if (rowClassRules && !rowClassRules.firstChange && this.GridApi) {
      this.refreshRowClassRules(this.GridApi);
    }

    if (reloadGridData) {
      if (this.rowModelType === 'serverSide') {
        this.mergeSuppliedAndAdditionalQueryData();
        this.GridApi?.refreshServerSide({ purge: true });
      }

      if (this.rowModelType === 'clientSide') {
        // Might want to change this as it redraws every row.
        this.GridApi?.redrawRows();
        setTimeout(() => {
          this.reloadGridDataChange.emit(false);
        });
      }
      this._cdr.markForCheck();
    }

    if (sortState) {
      const { currentValue } = sortState;
      if (currentValue) {
        this.handleSortState();
        this._cdr.markForCheck();
      }
    }

    if (resetFilterModel) {
      this.GridApi?.setFilterModel({});
      this._cdr.markForCheck();
    }

    // If supplied grid query data has changed, reload the grid.
    // This will be used for the universal search functionality.
    if (suppliedQueryData) {
      this.mergeSuppliedAndAdditionalQueryData();
      this.GridApi?.refreshServerSide({ purge: true });
      this._cdr.markForCheck();
    }

    // If the filter model passed into this component changes, handle it accordingly.
    if (FilterModel) {
      this.restoreOriginalGridState().subscribe();
      this.handleFilterModel();
      this._cdr.markForCheck();
    }
  }

  universalSearchChange(text: string) {
    Object.assign(this.UniversalSearch, { text });
    this.searchSubject.next(text);
  }

  resetAllFilters() {
    this.showFilteredEmptyGrid = false;
    this.GridApi?.setFilterModel(null);
    Object.assign(this.UniversalSearch, { text: '' });
    this.GridApi?.refreshServerSide({ purge: true });
    this.resetAllResultFilters.emit(true);
  }

  gridRowClicked(event: RowClickedEvent) {
    const { data } = event;
    this.rowClick.emit({ data });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
    delete this.GridApi;
  }
}
